import React, { useState, useMemo } from "react";
import Typography from "components/typography";
import Switch from "components/switch";
import { useAppTranslation } from "locale/useAppTranslation";
import {
  useSystemConfigServiceV1GetLoaConfigsByWorkspaceAndCompanyQuery,
  useSystemConfigServiceV1ActivateLoaConfigMutation,
  useSystemConfigServiceV1DeactiveLoaConfigMutation,
} from "_services/modules/system-config";

export const loaPageRouteName = "/master-data/layer-of-approval";

const LoAPage: React.FC = () => {
  const { t } = useAppTranslation("loa");
  const { data, isLoading } = useSystemConfigServiceV1GetLoaConfigsByWorkspaceAndCompanyQuery({
    typeCode: "TMS",
  });

  const [activateLoaConfig] = useSystemConfigServiceV1ActivateLoaConfigMutation();
  const [deactivateLoaConfig] = useSystemConfigServiceV1DeactiveLoaConfigMutation();
  const [optimisticUpdates, setOptimisticUpdates] = useState<Record<string, boolean>>({});

  const handleToggle = async (id: string, isCurrentlyActive: boolean) => {
    const previousValue = isCurrentlyActive;
    
    // Optimistic update
    setOptimisticUpdates((prev) => ({
      ...prev,
      [id]: !previousValue,
    }));

    try {
      if (isCurrentlyActive) {
        await deactivateLoaConfig({ id }).unwrap();
      } else {
        await activateLoaConfig({ id }).unwrap();
      }
    } catch (error) {
      // Rollback on error
      setOptimisticUpdates((prev) => ({
        ...prev,
        [id]: previousValue,
      }));
      console.error("Failed to toggle LOA config:", error);
    }
  };

  const features = useMemo(() => {
    const baseFeatures = data?.data || [];
    return baseFeatures.map((f) => ({
      ...f,
      is_active: optimisticUpdates[f.id || ""] !== undefined 
        ? optimisticUpdates[f.id || ""] 
        : f.is_active,
    }));
  }, [data, optimisticUpdates]);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <Typography variant="heading2">{t("title") || "Layer of Approval"}</Typography>
      </div>

      <section className="rounded-2xl border border-border bg-white shadow-sm p-6">
        {isLoading ? (
          <Typography variant="body">Loading...</Typography>
        ) : (
          <div className="flex flex-col gap-3">
            {features.map((f) => (
              <div key={f.id} className="flex items-center justify-between py-3 border-b last:border-b-0">
                <div>
                  <Typography variant="body" as="span">
                    {f.name}
                  </Typography>
                </div>
                <div>
                  <Switch
                    checked={f.is_active || false}
                    onChange={() => handleToggle(f.id || "", f.is_active || false)}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default LoAPage;
